import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Bell, CheckCheck, Archive, ArchiveRestore, Trash2, Inbox } from 'lucide-react';
import { fmtDateTime } from '@/lib/format';
import { Link } from 'react-router-dom';
import { useSimpleLabel } from '@/lib/prefs';

/**
 * NotificationsPage — inbox + archive.
 *
 * Two tabs: `inbox` (default, hides archived) and `archived` (only archived).
 * Per-row actions vary by tab:
 *   * inbox row (read):       Archive · Delete
 *   * inbox row (unread):     Archive · Delete   (marks read on click)
 *   * archived row:           Unarchive · Delete
 * Bulk header actions vary too:
 *   * inbox:    Mark all read · Archive all read · Delete all read
 *   * archived: nothing bulk (safer — user unarchives one at a time)
 */
export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('inbox');
  const [busy, setBusy] = useState(false);
  const t = useSimpleLabel('notifications');

  const load = async (view = tab) => {
    try {
      const r = await api.get(`/notifications?view=${view}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(parseErr(e));
    }
  };
  useEffect(() => { load(tab); }, [tab]);

  const readCount = useMemo(() => items.filter(i => i.read).length, [items]);
  const unreadCount = useMemo(() => items.filter(i => !i.read).length, [items]);

  // --- bulk actions
  const markAll = async () => {
    try { await api.post('/notifications/mark_read', { all: true }); toast.success('All marked read'); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };
  const archiveAllRead = async () => {
    if (!readCount) return toast.message('Nothing read to archive.');
    setBusy(true);
    try {
      const r = await api.post('/notifications/archive', { all: true });
      toast.success(`Archived ${r.data.archived}`);
      load();
    } catch (e) { toast.error(parseErr(e)); } finally { setBusy(false); }
  };
  const deleteAllRead = async () => {
    if (!readCount) return toast.message('Nothing read to delete.');
    if (!window.confirm(`Delete ${readCount} read notification${readCount === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post('/notifications/delete', { all: true });
      toast.success(`Deleted ${r.data.deleted}`);
      load();
    } catch (e) { toast.error(parseErr(e)); } finally { setBusy(false); }
  };

  // --- row actions
  const markOne = (n) => {
    if (n.read) return;
    api.post('/notifications/mark_read', { ids: [n.id] }).catch(() => {});
    setItems(prev => prev.map(i => (i.id === n.id ? { ...i, read: true } : i)));
  };
  const archiveOne = async (n) => {
    setItems(prev => prev.filter(i => i.id !== n.id)); // optimistic
    try {
      await api.post('/notifications/archive', { ids: [n.id] });
      toast.success('Archived');
    } catch (e) { toast.error(parseErr(e)); load(); }
  };
  const unarchiveOne = async (n) => {
    setItems(prev => prev.filter(i => i.id !== n.id));
    try {
      await api.post('/notifications/unarchive', { ids: [n.id] });
      toast.success('Restored to inbox');
    } catch (e) { toast.error(parseErr(e)); load(); }
  };
  const deleteOne = async (n) => {
    if (!window.confirm('Delete this notification permanently?')) return;
    setItems(prev => prev.filter(i => i.id !== n.id));
    try {
      await api.post('/notifications/delete', { ids: [n.id] });
      toast.success('Deleted');
    } catch (e) { toast.error(parseErr(e)); load(); }
  };

  const isArchivedView = tab === 'archived';

  return (
    <AppShell breadcrumbs={[{ label: t('header', 'Notifications') }]}>
      <div className="p-6 max-w-[900px] mx-auto" data-testid="notifications-page">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl font-semibold">{t('header', 'Notifications')}</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">Only events you&rsquo;re responsible for, accountable to, or explicitly named in.</p>
          </div>
          {!isArchivedView && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button variant="outline" size="sm" onClick={markAll} disabled={busy || unreadCount === 0} data-testid="notif-mark-all">
                <CheckCheck size={14} className="mr-1" />{t('mark_all_read', 'Mark all read')}
              </Button>
              <Button variant="outline" size="sm" onClick={archiveAllRead} disabled={busy || readCount === 0} data-testid="notif-archive-all-read">
                <Archive size={14} className="mr-1" />Archive read
              </Button>
              <Button variant="ghost" size="sm" onClick={deleteAllRead} disabled={busy || readCount === 0} data-testid="notif-delete-all-read"
                      className="text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]">
                <Trash2 size={14} className="mr-1" />Delete read
              </Button>
            </div>
          )}
        </div>

        <div className="mb-3 flex items-center gap-1 text-[13px]" data-testid="notif-tabs">
          <button
            onClick={() => setTab('inbox')}
            data-testid="notif-tab-inbox"
            className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors ${tab === 'inbox' ? 'bg-[hsl(var(--surface-2))] font-semibold' : 'text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--surface-1))]'}`}
          >
            <Inbox size={13} />Inbox
          </button>
          <button
            onClick={() => setTab('archived')}
            data-testid="notif-tab-archived"
            className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors ${tab === 'archived' ? 'bg-[hsl(var(--surface-2))] font-semibold' : 'text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--surface-1))]'}`}
          >
            <Archive size={13} />Archived
          </button>
        </div>

        <div className="border rounded-xl bg-[hsl(var(--card))] divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
          {items.length === 0 && (
            <div className="p-6 text-[hsl(var(--foreground-muted))]" data-testid="notif-empty">
              {isArchivedView ? 'Nothing archived.' : t('empty', 'All caught up.')}
            </div>
          )}
          {items.map(n => (
            <div key={n.id}
                 className={`px-4 py-3 hover:bg-[hsl(var(--surface-1))] ${!n.read && !isArchivedView ? 'bg-[hsl(var(--surface-1))]' : ''}`}
                 data-testid={`notification-item-${n.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-none"
                     style={{ background: n.kind === 'warning' ? 'hsl(var(--warning-bg))' : n.kind === 'success' ? 'hsl(var(--success-bg))' : 'hsl(var(--info-bg))' }}>
                  <Bell size={14} />
                </div>
                <Link
                  to={n.link || '/notifications'}
                  onClick={() => markOne(n)}
                  className="flex-1 min-w-0 block"
                >
                  <div className="text-[13.5px] font-medium">{n.title}</div>
                  {n.body && <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">{n.body}</div>}
                  <div className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-1">{fmtDateTime(n.created_at)}</div>
                </Link>
                <div className="flex items-center gap-1 flex-none opacity-70 hover:opacity-100 transition-opacity">
                  {isArchivedView ? (
                    <button
                      onClick={() => unarchiveOne(n)}
                      className="p-1.5 rounded hover:bg-[hsl(var(--surface-2))] text-[hsl(var(--foreground-muted))]"
                      title="Restore to inbox"
                      data-testid={`notif-unarchive-${n.id}`}
                    >
                      <ArchiveRestore size={14} />
                    </button>
                  ) : (
                    <button
                      onClick={() => archiveOne(n)}
                      className="p-1.5 rounded hover:bg-[hsl(var(--surface-2))] text-[hsl(var(--foreground-muted))]"
                      title="Archive (hide from inbox, keep on file)"
                      data-testid={`notif-archive-${n.id}`}
                    >
                      <Archive size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => deleteOne(n)}
                    className="p-1.5 rounded hover:bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]"
                    title="Delete permanently"
                    data-testid={`notif-delete-${n.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
